import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useTranslation } from "react-i18next"
import { ArrowLeft, FileText } from "lucide-react"
import { useTask, useCreateTask, useUpdateTask } from "@/hooks/use-tasks"
import { useTaskTemplates, useCreateTaskFromTemplate } from "@/hooks/use-task-templates"
import { useTaskCategories } from "@/hooks/use-task-categories"
import { RecurrenceEditor } from "@/components/recurrence-editor"
import { ApiClientError } from "@/lib/api-client"
import { Button } from "@/components/ui/button"
import { toast } from "sonner"
import type { RecurrenceRule } from "@/lib/types"

const taskSchema = z.object({
  title: z.string().min(1, "Tytuł jest wymagany"),
  description: z.string().optional(),
  task_type: z.string().optional(),
  priority: z.string().optional(),
  status: z.string().optional(),
  due_date: z.string().optional(),
  due_time: z.string().optional(),
  assignee_id: z.string().optional(),
  parent_task_id: z.string().optional(),
  task_category_id: z.string().optional(),
})

type TaskFormData = z.infer<typeof taskSchema>

export function TaskFormPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = !!id
  const [error, setError] = useState<string | null>(null)
  const [recurrenceRule, setRecurrenceRule] = useState<RecurrenceRule | null>(null)
  const [recurrenceEndDate, setRecurrenceEndDate] = useState<string | null>(null)
  const [showTemplates, setShowTemplates] = useState(false)

  const { data: taskData } = useTask(id)
  const { data: templatesData } = useTaskTemplates()
  const { data: categoriesData } = useTaskCategories()
  const taskCategories = categoriesData?.data ?? []
  const createMutation = useCreateTask()
  const updateMutation = useUpdateTask()
  const createFromTemplateMutation = useCreateTaskFromTemplate()

  const templates = templatesData?.data ?? []

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<TaskFormData>({
    resolver: zodResolver(taskSchema),
    values: isEdit && taskData ? {
      title: taskData.data.title,
      description: taskData.data.description || "",
      task_type: taskData.data.task_type || "",
      priority: taskData.data.priority,
      status: taskData.data.status,
      due_date: taskData.data.due_date || "",
      due_time: taskData.data.due_time || "",
      assignee_id: taskData.data.assignee?.id || "",
      parent_task_id: taskData.data.parent_task_id || "",
      task_category_id: taskData.data.task_category_id || "",
    } : {
      title: "",
      description: "",
      task_type: "task",
      priority: "normal",
      status: "open",
      due_date: "",
      due_time: "",
      assignee_id: "",
      parent_task_id: "",
      task_category_id: "",
    },
  })

  // Initialize recurrence from existing task
  useState(() => {
    if (isEdit && taskData?.data?.recurrence_rule) {
      setRecurrenceRule(taskData.data.recurrence_rule)
      setRecurrenceEndDate(taskData.data.recurrence_end_date)
    }
  })

  const handleCreateFromTemplate = async (templateId: string) => {
    try {
      await createFromTemplateMutation.mutateAsync({ templateId })
      toast.success("Zadanie utworzone z szablonu")
      navigate("/tasks")
    } catch (err) {
      if (err instanceof ApiClientError) toast.error(err.message)
      else toast.error("Nie udało się utworzyć zadania")
    }
  }

  const onSubmit = async (data: TaskFormData) => {
    try {
      setError(null)
      const payload: Record<string, unknown> = {
        ...data,
        assignee_id: data.assignee_id || null,
        parent_task_id: data.parent_task_id || null,
        task_category_id: data.task_category_id || null,
        due_date: data.due_date || null,
        due_time: data.due_time || null,
        recurrence_rule: recurrenceRule,
        recurrence_end_date: recurrenceEndDate,
      }

      if (isEdit) {
        await updateMutation.mutateAsync({ id, data: payload })
        toast.success("Zadanie zaktualizowane")
      } else {
        await createMutation.mutateAsync(payload)
        toast.success("Zadanie utworzone")
      }
      navigate("/tasks")
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message)
      else setError("Wystąpił nieoczekiwany błąd")
    }
  }

  const inputClass = "h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm text-foreground outline-none"

  return (
    <div className="mx-auto max-w-[720px] space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted/50">
            <ArrowLeft size={18} />
          </button>
          <h1 className="text-xl font-semibold text-foreground">
            {isEdit ? "Edytuj zadanie" : t("tasks.new")}
          </h1>
        </div>
        {!isEdit && templates.length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowTemplates(!showTemplates)}
          >
            <FileText size={14} />
            {showTemplates ? "Ukryj szablony" : "Z szablonu"}
          </Button>
        )}
      </div>

      {/* Template selector */}
      {showTemplates && templates.length > 0 && (
        <div className="rounded-lg border border-border p-4 space-y-3">
          <h2 className="text-sm font-semibold text-foreground">Wybierz szablon</h2>
          <div className="grid grid-cols-2 gap-2">
            {templates.map((tpl) => (
              <button
                key={tpl.id}
                type="button"
                onClick={() => handleCreateFromTemplate(tpl.id)}
                className="rounded-lg border border-border p-3 text-left transition-colors hover:bg-muted/30"
              >
                <p className="text-sm font-medium text-foreground">{tpl.name}</p>
                {tpl.description && (
                  <p className="text-xs mt-0.5 text-muted-foreground">{tpl.description}</p>
                )}
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-xs text-muted-foreground">{tpl.task_type}</span>
                  {tpl.template_items_count > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {tpl.template_items_count} elementów checklisty
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic info */}
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase text-muted-foreground">Dane zadania</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Tytuł *</label>
              <input {...register("title")} className={`${inputClass} ${errors.title ? "border-destructive" : ""}`}
                placeholder="np. Przygotować ofertę dla klienta" />
              {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">{t("tasks.priority")}</label>
              <select {...register("priority")} className={inputClass}>
                <option value="low">Niski</option>
                <option value="normal">Normalny</option>
                <option value="high">Wysoki</option>
                <option value="urgent">Pilny</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">{t("tasks.status")}</label>
              <select {...register("status")} className={inputClass}>
                <option value="open">Otwarte</option>
                <option value="in_progress">W toku</option>
              </select>
            </div>
          </div>
        </section>

        {/* Schedule */}
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase text-muted-foreground">Termin</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">{t("tasks.dueDate")}</label>
              <input {...register("due_date")} type="date" className={inputClass} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Godzina</label>
              <input {...register("due_time")} type="time" className={inputClass} />
            </div>
          </div>
        </section>

        {/* Recurrence */}
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase text-muted-foreground">Powtarzalność</h2>
          <RecurrenceEditor
            value={recurrenceRule}
            endDate={recurrenceEndDate}
            onChange={(rule, endDate) => {
              setRecurrenceRule(rule)
              setRecurrenceEndDate(endDate)
            }}
          />
        </section>

        {/* Additional */}
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase text-muted-foreground">Dodatkowe</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-muted-foreground">Typ zadania</label>
                <select {...register("task_type")} className={inputClass}>
                  <option value="task">Zadanie</option>
                  <option value="call">Telefon</option>
                  <option value="meeting">Spotkanie</option>
                  <option value="email">Email</option>
                  <option value="followup">Follow-up</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-muted-foreground">Kategoria</label>
                <select {...register("task_category_id")} className={inputClass}>
                  <option value="">Brak</option>
                  {taskCategories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Opis</label>
              <textarea {...register("description")} rows={3}
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm text-foreground outline-none" />
            </div>
          </div>
        </section>

        {/* Actions */}
        <div className="flex justify-end gap-3 border-t border-border pt-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? t("common.loading") : t("common.save")}
          </Button>
        </div>
      </form>
    </div>
  )
}
